import { z } from 'zod';
import { MODALIDADE, MODALIDADE_PERSONALIZADA } from '../dominio/index.js';
/**
 * A modalidade — e o nome que a arena escolheu quando nenhuma da lista
 * serve.
 *
 * O vocabulário é lista FECHADA no banco (um CHECK por tabela) porque é
 * ele que a agenda, o plano e o relatório cruzam. Mas uma arena faz
 * "ritmos", "treino de saque", "aulão de sexta" — e esperar deploy para
 * cadastrar uma turma é o tipo de atrito que faz a planilha voltar. A
 * saída é um valor da lista, `Outra`, que exige um NOME junto.
 *
 * O par vive assim em toda tabela que declara modalidade:
 *
 *   modalidade        'Futvolei' … 'Outra'   — o que o CHECK confere
 *   modalidadeOutra   texto, só em 'Outra'  — o que a tela mostra
 *
 * Quem desenha rótulo nunca lê `ROTULO_MODALIDADE` direto: lê
 * `rotuloDaModalidade(modalidade, modalidadeOutra)`, que devolve o nome
 * digitado quando a modalidade é personalizada. Ler o Record direto num
 * registro 'Outra' mostraria a palavra "Outra" na tela.
 */
export const ROTULO_MODALIDADE = {
    Futvolei: 'Futevôlei',
    Volei: 'Vôlei',
    BeachTennis: 'Beach Tennis',
    Funcional: 'Funcional',
    Livre: 'Livre',
    Outra: 'Outra',
};
export const NOME_PERSONALIZADO_MINIMO = 2;
export const NOME_PERSONALIZADO_MAXIMO = 40;
/** O nome digitado quando a modalidade é `Outra`. */
export const zNomeDeModalidade = z
    .string()
    .trim()
    .min(NOME_PERSONALIZADO_MINIMO, 'Dê um nome à modalidade.')
    .max(NOME_PERSONALIZADO_MAXIMO, `No máximo ${NOME_PERSONALIZADO_MAXIMO} caracteres.`);
export const zModalidade = z.enum(MODALIDADE, {
    errorMap: () => ({ message: 'Escolha uma das modalidades.' }),
});
/**
 * Os dois campos juntos, para espalhar com `...` em quem declara
 * modalidade: `zNovaTurma`, `zPlano`.
 *
 * `nullable` e não só `optional` porque o PATCH precisa APAGAR o nome ao
 * sair de `Outra` para uma modalidade da lista.
 */
export const camposDeModalidade = {
    modalidade: zModalidade,
    modalidadeOutra: zNomeDeModalidade.nullable().optional(),
};
/**
 * A regra entre os dois campos, para o `superRefine` de quem os usa.
 *
 * `Outra` sem nome seria uma turma chamada "Outra"; nome com modalidade
 * da lista seria um nome que a tela nunca mostra — os dois são recusados
 * na borda, e o CHECK da migration repete a regra no banco.
 */
export function conferirModalidade(valor, ctx) {
    if (valor.modalidade === MODALIDADE_PERSONALIZADA) {
        if (!valor.modalidadeOutra) {
            ctx.addIssue({
                code: 'custom',
                path: ['modalidadeOutra'],
                message: 'Dê um nome à modalidade personalizada.',
            });
        }
        return;
    }
    if (valor.modalidade !== undefined && valor.modalidadeOutra) {
        ctx.addIssue({
            code: 'custom',
            path: ['modalidadeOutra'],
            message: 'O nome livre vale só para a modalidade personalizada.',
        });
    }
}
/** O que a tela mostra: o nome digitado em `Outra`, o rótulo nos demais. */
export function rotuloDaModalidade(modalidade, modalidadeOutra) {
    return modalidade === MODALIDADE_PERSONALIZADA && modalidadeOutra
        ? modalidadeOutra
        : ROTULO_MODALIDADE[modalidade];
}
/**
 * As modalidades que a ALOCAÇÃO FIXA aceita (pendência P1 do documento 10).
 *
 * A personalizada fica fora de propósito: a alocação é o acordo de que
 * "terça às 19h é do vôlei na Quadra 2", e um acordo sobre "Outra" não diz
 * nada a quem lê a mensagem de conflito. Turma personalizada continua
 * existindo — ela só não reserva horário no nome de uma modalidade.
 */
export const MODALIDADES_ALOCAVEIS = [
    'Futvolei',
    'Volei',
    'BeachTennis',
    'Funcional',
    'Livre',
];
/** A modalidade é a personalizada? Evita comparar com a string em 20 lugares. */
export function ehModalidadePersonalizada(modalidade) {
    return modalidade === MODALIDADE_PERSONALIZADA;
}
//# sourceMappingURL=modalidades.js.map